import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { useCallback, useEffect, useRef, useState } from 'react';
import { ToastService } from '../../../AppToast';
import { translateError } from '../../../shared/language';
import { PetrolimexLogService } from '../../danh-muc/danh-sach-giao-dich-xang-dau/PetrolimexLogService';

const POLLING_INTERVAL_MS = 1000;
const DEFAULT_PREVIEW_HEIGHT = 800;

export const DialogQrCode = ({ display, setDisplay, selectedItems }) => {
  const [html, setHtml] = useState('');
  const [previewHeight, setPreviewHeight] = useState(DEFAULT_PREVIEW_HEIGHT);
  const iframeRef = useRef<HTMLIFrameElement>(null);

  const resizePreview = useCallback(() => {
    const measure = () => {
      const doc = iframeRef.current?.contentDocument;
      if (!doc?.body) {
        return;
      }

      const height = Math.max(
        doc.body.scrollHeight,
        doc.documentElement?.scrollHeight ?? 0,
      );

      if (height > 0) {
        setPreviewHeight(height);
      }
    };

    measure();
    requestAnimationFrame(measure);
  }, []);

  useEffect(() => {
    if (!display) {
      setPreviewHeight(DEFAULT_PREVIEW_HEIGHT);
    }
  }, [display]);

  useEffect(() => {
    if (!display || !selectedItems?.id) {
      setHtml('');
      return;
    }

    const logId = selectedItems.id;
    let cancelled = false;
    let timeoutId: ReturnType<typeof setTimeout> | null = null;

    const stopPolling = () => {
      cancelled = true;
      if (timeoutId) {
        clearTimeout(timeoutId);
        timeoutId = null;
      }
    };

    const handleError = (err: unknown, fallbackMessage: string) => {
      if (!cancelled) {
        const response = (err as { response?: unknown })?.response;
        const message = response ? translateError(response) : fallbackMessage;
        ToastService.error(message);
        setDisplay(null);
      }
      stopPolling();
    };

    const loadPaymentLog = async (): Promise<boolean> => {
      try {
        const data = await PetrolimexLogService.printPaymentLogSilent({ id: logId });
        if (!cancelled && data?.masterPage) {
          setHtml(data.masterPage);
        }
        return true;
      } catch (err) {
        handleError(err, 'Không thể tạo mã QR thanh toán.');
        return false;
      }
    };

    const pollPaymentStatus = async () => {
      if (cancelled) {
        return;
      }

      try {
        const status = await PetrolimexLogService.getPaymentStatusSilent({ id: logId });

        if (cancelled) {
          return;
        }

        if (status?.isPaymented) {
          await loadPaymentLog();
          stopPolling();
          return;
        }

        if (!cancelled) {
          timeoutId = setTimeout(pollPaymentStatus, POLLING_INTERVAL_MS);
        }
      } catch (err) {
        handleError(err, 'Không thể kiểm tra trạng thái thanh toán.');
      }
    };

    const init = async () => {
      const loaded = await loadPaymentLog();
      if (loaded && !cancelled) {
        timeoutId = setTimeout(pollPaymentStatus, POLLING_INTERVAL_MS);
      }
    };

    void init();

    return stopPolling;
  }, [display, selectedItems?.id, setDisplay]);

  const footer = (
    <div>
      <Button label="Đóng" onClick={() => setDisplay(null)} className="p-button-outlined p-button-secondary p-button-sm" />
    </div>
  );

  return (
    <Dialog
      header="Thông tin giao dịch"
      footer={footer}
      visible={!!display}
      style={{ width: '60rem', height: '90rem' }}
      contentStyle={{ overflow: 'auto' }}
      position="top"
      onHide={() => setDisplay(null)}
    >
      <iframe
        ref={iframeRef}
        title="template-preview"
        className="template-preview w-full"
        style={{ height: `${previewHeight}px` }}
        srcDoc={html}
        onLoad={resizePreview}
      />
    </Dialog>
  );
};
